/**
 * SellerCreateDialog — RHF + Zod "Create seller" form (SCRUM-155). POSTs the
 * SHIPPED `/admin/marketplace/sellers` route (STORY-003-001 AC #1) with a
 * `SellerCreate` subset. Mirrors `MarketFormDialog`'s create mechanics and
 * `SellerEditForm`'s field anatomy; `market_id` is picked from `useMarkets`
 * (read-only import of the existing admin-markets hook — no new backend
 * route, no changes to that feature's files).
 *
 * Soft-uniqueness on `(market_id, pavilion_number)`: the backend returns 409
 * `marketplace.seller.duplicate_pavilion` (a warning, not a hard block) —
 * this form surfaces an inline "create anyway?" confirmation that resubmits
 * with `force=true` per the shipped contract (Addendum-adjacent story text,
 * STORY-003-001 "Soft-uniqueness" section).
 */
import { useCallback, useMemo, useState, type ReactElement } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";

import { BottomSheet, Button, Field } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { useIsDesktop } from "@/features/chat/useIsDesktop";
import { ApiError } from "@/lib/api-client";
import { useMarkets } from "@/features/admin/markets/hooks";
import { useCreateSeller } from "@/features/admin/sellers/hooks";
import type { AdminSellerCreate } from "@/features/admin/sellers/api";

// E.164: leading '+', then a non-zero digit, then 7-14 more digits (total
// 8-15 digits after the '+'). MUST mirror the backend's `_E164_RE`
// (apps/api/garden/features/marketplace/schemas.py `is_e164`) exactly — the
// backend rejects a leading zero right after '+' (ITU-T E.164 never starts
// a country code with 0), which this schema previously did NOT check
// (`/^\+\d{8,15}$/`, no restriction on the first digit). That gap let a
// very common real-world mistake — typing a LOCAL number with its
// leading-zero trunk prefix and just prepending '+' instead of replacing it
// with the country code (e.g. "+0700123456" instead of "+996700123456") —
// pass client validation, fail server-side, and surface as an opaque
// "Something went wrong" toast instead of an inline field error.
const E164_RE = /^\+[1-9]\d{7,14}$/;

const sellerCreateSchema = z.object({
  business_name: z.string().min(1, "formError.required").max(200),
  market_id: z.string().min(1, "formError.required"),
  pavilion_number: z.string().min(1, "formError.required").max(64),
  phone_e164: z.string().regex(E164_RE, "formError.phone"),
  // Optional — empty is valid, but a NON-empty value must be real E.164 too
  // (the backend's _validate_seller_phones checks it the same way).
  whatsapp_phone_e164: z
    .string()
    .max(20)
    .refine((v) => v === "" || E164_RE.test(v), "formError.phone"),
  telegram_handle: z.string().max(64),
  entrance_hint: z.string().max(500),
  verification_notes: z.string().max(1000),
});
type SellerCreateValues = z.infer<typeof sellerCreateSchema>;

function nullIfBlank(v: string): string | null {
  const t = v.trim();
  return t === "" ? null : t;
}

export interface SellerCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const SELECT_CLASS =
  "min-h-[44px] w-full rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)]";

export function SellerCreateDialog({
  open,
  onClose,
  onSuccess,
}: SellerCreateDialogProps): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const isDesktop = useIsDesktop();

  const { data: marketPages } = useMarkets();
  const markets = useMemo(
    () => marketPages?.pages.flatMap((p) => p.markets) ?? [],
    [marketPages],
  );

  const create = useCreateSeller();
  const [duplicateConfirm, setDuplicateConfirm] = useState<AdminSellerCreate | null>(
    null,
  );

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<SellerCreateValues>({
    resolver: zodResolver(sellerCreateSchema),
    defaultValues: {
      business_name: "",
      market_id: "",
      pavilion_number: "",
      phone_e164: "",
      whatsapp_phone_e164: "",
      telegram_handle: "",
      entrance_hint: "",
      verification_notes: "",
    },
  });

  const closeAndReset = useCallback(() => {
    setDuplicateConfirm(null);
    reset();
    onClose();
  }, [reset, onClose]);

  const handleError = useCallback(
    (err: unknown, body: AdminSellerCreate) => {
      if (err instanceof ApiError && err.code === "marketplace.seller.duplicate_pavilion") {
        setDuplicateConfirm(body);
        return;
      }
      // Field-specific server-side validation errors (E.164 phone shape,
      // dead market_id, a phone already in use by another live seller) —
      // surface them as an INLINE field error instead of an opaque generic
      // toast. Belt-and-suspenders: the client-side zod schema now mirrors
      // the backend's E.164 rule exactly, so `phone_invalid_e164` shouldn't
      // normally reach here, but any future client/server validation drift
      // (or a field this dialog doesn't validate as strictly) still gets a
      // useful, field-anchored message instead of "Something went wrong."
      // `.message` here is an i18n KEY, not a resolved string — matching the
      // zod schema's convention above, so `errText()` translates it exactly
      // like a client-side validation failure.
      if (err instanceof ApiError) {
        if (err.code === "marketplace.seller.phone_invalid_e164") {
          const badField = err.problem["errors"];
          const field =
            Array.isArray(badField) &&
            badField[0] &&
            typeof badField[0] === "object" &&
            "field" in badField[0] &&
            badField[0].field === "whatsapp_phone_e164"
              ? "whatsapp_phone_e164"
              : "phone_e164";
          setError(field, { type: "server", message: "formError.phone" });
          return;
        }
        if (err.code === "marketplace.seller.duplicate_phone") {
          setError("phone_e164", {
            type: "server",
            message: "sellers.create.duplicatePhone",
          });
          return;
        }
        if (err.code === "marketplace.seller.market_id_invalid") {
          setError("market_id", {
            type: "server",
            message: "sellers.create.invalidMarket",
          });
          return;
        }
      }
      toast.error(t("error.generic"));
    },
    [setError, t],
  );

  const submit = useCallback(
    (values: SellerCreateValues) => {
      const body: AdminSellerCreate = {
        business_name: values.business_name.trim(),
        market_id: values.market_id,
        pavilion_number: values.pavilion_number.trim(),
        phone_e164: values.phone_e164.trim(),
        whatsapp_phone_e164: nullIfBlank(values.whatsapp_phone_e164),
        telegram_handle: nullIfBlank(values.telegram_handle),
        entrance_hint: nullIfBlank(values.entrance_hint),
        verification_notes: nullIfBlank(values.verification_notes),
      };
      setDuplicateConfirm(null);
      create.mutate(
        { body },
        {
          onSuccess: () => {
            toast.success(t("sellers.create.success"));
            closeAndReset();
            onSuccess();
          },
          onError: (err) => handleError(err, body),
        },
      );
    },
    [create, closeAndReset, onSuccess, handleError, t],
  );

  const confirmForceCreate = useCallback(() => {
    if (!duplicateConfirm) return;
    create.mutate(
      { body: duplicateConfirm, force: true },
      {
        onSuccess: () => {
          toast.success(t("sellers.create.success"));
          closeAndReset();
          onSuccess();
        },
        onError: () => toast.error(t("error.generic")),
      },
    );
  }, [duplicateConfirm, create, closeAndReset, onSuccess, t]);

  const errText = (key?: string): string | undefined => (key ? t(key) : undefined);

  return (
    <BottomSheet
      open={open}
      onClose={closeAndReset}
      title={t("sellers.create.title")}
      desktop={isDesktop}
    >
      <form
        data-testid="seller-create-form"
        onSubmit={handleSubmit(submit)}
        className="flex flex-col gap-4"
      >
        <Field
          label={t("sellers.create.businessName")}
          data-testid="seller-create-business_name"
          error={errText(errors.business_name?.message)}
          {...register("business_name")}
        />

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="seller-create-market"
            className="text-[13px] font-semibold text-foreground"
          >
            {t("sellers.create.market")}
          </label>
          <select
            id="seller-create-market"
            data-testid="seller-create-market_id"
            className={SELECT_CLASS}
            {...register("market_id")}
          >
            <option value="">{t("sellers.create.marketPlaceholder")}</option>
            {markets.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name_ru}
              </option>
            ))}
          </select>
          {errors.market_id ? (
            <p className="text-xs text-[var(--g-error)]">
              {errText(errors.market_id.message)}
            </p>
          ) : null}
        </div>

        <Field
          label={t("sellers.create.pavilion")}
          data-testid="seller-create-pavilion_number"
          error={errText(errors.pavilion_number?.message)}
          {...register("pavilion_number")}
        />
        <Field
          label={t("sellers.create.phone")}
          data-testid="seller-create-phone_e164"
          error={errText(errors.phone_e164?.message)}
          {...register("phone_e164")}
        />
        <Field
          label={t("sellers.create.whatsapp")}
          data-testid="seller-create-whatsapp"
          error={errText(errors.whatsapp_phone_e164?.message)}
          {...register("whatsapp_phone_e164")}
        />
        <Field
          label={t("sellers.create.telegram")}
          data-testid="seller-create-telegram"
          {...register("telegram_handle")}
        />

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="seller-create-hint"
            className="text-[13px] font-semibold text-foreground"
          >
            {t("sellers.create.entranceHint")}
          </label>
          <textarea
            id="seller-create-hint"
            data-testid="seller-create-entrance_hint"
            rows={2}
            maxLength={500}
            className="w-full resize-none rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)]"
            {...register("entrance_hint")}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="seller-create-notes"
            className="text-[13px] font-semibold text-foreground"
          >
            {t("sellers.create.notes")}
          </label>
          <textarea
            id="seller-create-notes"
            data-testid="seller-create-verification_notes"
            rows={2}
            maxLength={1000}
            className="w-full resize-none rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)]"
            {...register("verification_notes")}
          />
        </div>

        {duplicateConfirm ? (
          <div
            data-testid="seller-create-duplicate-warning"
            role="alert"
            className="rounded-[var(--g-radius-sm)] border border-[var(--g-warn)] bg-[var(--g-warn)]/10 p-3 text-sm text-[var(--g-warn)]"
          >
            <p>{t("sellers.create.duplicatePavilionWarning")}</p>
            <div className="mt-2 flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setDuplicateConfirm(null)}
                className="min-h-[44px]"
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                data-testid="seller-create-duplicate-confirm"
                loading={create.isPending}
                onClick={confirmForceCreate}
                className="min-h-[44px]"
              >
                {t("sellers.create.duplicatePavilionConfirm")}
              </Button>
            </div>
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={closeAndReset}
            disabled={create.isPending}
            className="min-h-[44px]"
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            data-testid="seller-create-submit"
            loading={create.isPending}
            className="min-h-[44px]"
          >
            {t("sellers.create.submit")}
          </Button>
        </div>
      </form>
    </BottomSheet>
  );
}

export default SellerCreateDialog;
